/** Weapons, armour, and packs needed to make a level-1 character playable. SRD 5.2. */

export type DamageType = 'slashing' | 'piercing' | 'bludgeoning'

export interface WeaponDef {
  id: string
  name: string
  category: 'simple' | 'martial'
  ranged: boolean
  damage: string        // "1d8"
  damageType: DamageType
  properties: string[]  // Finesse, Light, Thrown (20/60), Versatile (1d10)…
  versatile?: string
  range?: string
  mastery: string
}

export const WEAPONS: WeaponDef[] = [
  { id: 'club', name: 'Club', category: 'simple', ranged: false, damage: '1d4', damageType: 'bludgeoning', properties: ['Light'], mastery: 'Slow' },
  { id: 'dagger', name: 'Dagger', category: 'simple', ranged: false, damage: '1d4', damageType: 'piercing', properties: ['Finesse', 'Light', 'Thrown'], range: '20/60', mastery: 'Nick' },
  { id: 'greatclub', name: 'Greatclub', category: 'simple', ranged: false, damage: '1d8', damageType: 'bludgeoning', properties: ['Two-Handed'], mastery: 'Push' },
  { id: 'handaxe', name: 'Handaxe', category: 'simple', ranged: false, damage: '1d6', damageType: 'slashing', properties: ['Light', 'Thrown'], range: '20/60', mastery: 'Vex' },
  { id: 'javelin', name: 'Javelin', category: 'simple', ranged: false, damage: '1d6', damageType: 'piercing', properties: ['Thrown'], range: '30/120', mastery: 'Slow' },
  { id: 'mace', name: 'Mace', category: 'simple', ranged: false, damage: '1d6', damageType: 'bludgeoning', properties: [], mastery: 'Sap' },
  { id: 'quarterstaff', name: 'Quarterstaff', category: 'simple', ranged: false, damage: '1d6', damageType: 'bludgeoning', properties: ['Versatile'], versatile: '1d8', mastery: 'Topple' },
  { id: 'sickle', name: 'Sickle', category: 'simple', ranged: false, damage: '1d4', damageType: 'slashing', properties: ['Light'], mastery: 'Nick' },
  { id: 'spear', name: 'Spear', category: 'simple', ranged: false, damage: '1d6', damageType: 'piercing', properties: ['Thrown', 'Versatile'], versatile: '1d8', range: '20/60', mastery: 'Sap' },
  { id: 'light-crossbow', name: 'Light Crossbow', category: 'simple', ranged: true, damage: '1d8', damageType: 'piercing', properties: ['Ammunition', 'Loading', 'Two-Handed'], range: '80/320', mastery: 'Slow' },
  { id: 'dart', name: 'Dart', category: 'simple', ranged: true, damage: '1d4', damageType: 'piercing', properties: ['Finesse', 'Thrown'], range: '20/60', mastery: 'Vex' },
  { id: 'shortbow', name: 'Shortbow', category: 'simple', ranged: true, damage: '1d6', damageType: 'piercing', properties: ['Ammunition', 'Two-Handed'], range: '80/320', mastery: 'Vex' },
  { id: 'sling', name: 'Sling', category: 'simple', ranged: true, damage: '1d4', damageType: 'bludgeoning', properties: ['Ammunition'], range: '30/120', mastery: 'Slow' },

  { id: 'battleaxe', name: 'Battleaxe', category: 'martial', ranged: false, damage: '1d8', damageType: 'slashing', properties: ['Versatile'], versatile: '1d10', mastery: 'Topple' },
  { id: 'flail', name: 'Flail', category: 'martial', ranged: false, damage: '1d8', damageType: 'bludgeoning', properties: [], mastery: 'Sap' },
  { id: 'glaive', name: 'Glaive', category: 'martial', ranged: false, damage: '1d10', damageType: 'slashing', properties: ['Heavy', 'Reach', 'Two-Handed'], mastery: 'Graze' },
  { id: 'greataxe', name: 'Greataxe', category: 'martial', ranged: false, damage: '1d12', damageType: 'slashing', properties: ['Heavy', 'Two-Handed'], mastery: 'Cleave' },
  { id: 'greatsword', name: 'Greatsword', category: 'martial', ranged: false, damage: '2d6', damageType: 'slashing', properties: ['Heavy', 'Two-Handed'], mastery: 'Graze' },
  { id: 'longsword', name: 'Longsword', category: 'martial', ranged: false, damage: '1d8', damageType: 'slashing', properties: ['Versatile'], versatile: '1d10', mastery: 'Sap' },
  { id: 'maul', name: 'Maul', category: 'martial', ranged: false, damage: '2d6', damageType: 'bludgeoning', properties: ['Heavy', 'Two-Handed'], mastery: 'Topple' },
  { id: 'rapier', name: 'Rapier', category: 'martial', ranged: false, damage: '1d8', damageType: 'piercing', properties: ['Finesse'], mastery: 'Vex' },
  { id: 'scimitar', name: 'Scimitar', category: 'martial', ranged: false, damage: '1d6', damageType: 'slashing', properties: ['Finesse', 'Light'], mastery: 'Nick' },
  { id: 'shortsword', name: 'Shortsword', category: 'martial', ranged: false, damage: '1d6', damageType: 'piercing', properties: ['Finesse', 'Light'], mastery: 'Vex' },
  { id: 'trident', name: 'Trident', category: 'martial', ranged: false, damage: '1d8', damageType: 'piercing', properties: ['Thrown', 'Versatile'], versatile: '1d10', range: '20/60', mastery: 'Topple' },
  { id: 'warhammer', name: 'Warhammer', category: 'martial', ranged: false, damage: '1d8', damageType: 'bludgeoning', properties: ['Versatile'], versatile: '1d10', mastery: 'Push' },
  { id: 'longbow', name: 'Longbow', category: 'martial', ranged: true, damage: '1d8', damageType: 'piercing', properties: ['Ammunition', 'Heavy', 'Two-Handed'], range: '150/600', mastery: 'Slow' },
  { id: 'heavy-crossbow', name: 'Heavy Crossbow', category: 'martial', ranged: true, damage: '1d10', damageType: 'piercing', properties: ['Ammunition', 'Heavy', 'Loading', 'Two-Handed'], range: '100/400', mastery: 'Push' },
]

export const WEAPONS_BY_ID = new Map(WEAPONS.map((w) => [w.id, w]))

export interface ArmorDef {
  id: string
  name: string
  category: 'light' | 'medium' | 'heavy' | 'shield'
  baseAc: number
  /** How much Dexterity applies: full, capped at 2, or none. */
  dexCap: number | null
  stealthDisadvantage: boolean
  strengthRequirement?: number
}

export const ARMOR: ArmorDef[] = [
  { id: 'padded', name: 'Padded Armor', category: 'light', baseAc: 11, dexCap: null, stealthDisadvantage: true },
  { id: 'leather', name: 'Leather Armor', category: 'light', baseAc: 11, dexCap: null, stealthDisadvantage: false },
  { id: 'studded-leather', name: 'Studded Leather Armor', category: 'light', baseAc: 12, dexCap: null, stealthDisadvantage: false },
  { id: 'hide', name: 'Hide Armor', category: 'medium', baseAc: 12, dexCap: 2, stealthDisadvantage: false },
  { id: 'chain-shirt', name: 'Chain Shirt', category: 'medium', baseAc: 13, dexCap: 2, stealthDisadvantage: false },
  { id: 'scale-mail', name: 'Scale Mail', category: 'medium', baseAc: 14, dexCap: 2, stealthDisadvantage: true },
  { id: 'breastplate', name: 'Breastplate', category: 'medium', baseAc: 14, dexCap: 2, stealthDisadvantage: false },
  { id: 'half-plate', name: 'Half Plate Armor', category: 'medium', baseAc: 15, dexCap: 2, stealthDisadvantage: true },
  { id: 'ring-mail', name: 'Ring Mail', category: 'heavy', baseAc: 14, dexCap: 0, stealthDisadvantage: true },
  { id: 'chain-mail', name: 'Chain Mail', category: 'heavy', baseAc: 16, dexCap: 0, stealthDisadvantage: true, strengthRequirement: 13 },
  { id: 'splint', name: 'Splint Armor', category: 'heavy', baseAc: 17, dexCap: 0, stealthDisadvantage: true, strengthRequirement: 15 },
  { id: 'plate', name: 'Plate Armor', category: 'heavy', baseAc: 18, dexCap: 0, stealthDisadvantage: true, strengthRequirement: 15 },
  { id: 'shield', name: 'Shield', category: 'shield', baseAc: 2, dexCap: 0, stealthDisadvantage: false },
]

export const ARMOR_BY_ID = new Map(ARMOR.map((a) => [a.id, a]))

/** Adventuring packs, expanded so the equipment box lists real contents. */
export const PACKS: Record<string, string[]> = {
  "Burglar's Pack": ['Backpack', 'Ball bearings (1,000)', '10 candles', 'Crowbar', 'Hooded lantern', '7 flasks of oil', 'Rope (50 feet)', 'Tinderbox', 'Rations (5 days)', 'Waterskin'],
  "Diplomat's Pack": ['Chest', 'Fine clothes', 'Ink', 'Ink pen', 'Lamp', 'Paper (5 sheets)', 'Perfume', '2 flasks of oil', 'Tinderbox'],
  "Dungeoneer's Pack": ['Backpack', 'Crowbar', 'Hammer', '10 pitons', 'Hooded lantern', '7 flasks of oil', 'Rope (50 feet)', 'Tinderbox', 'Rations (10 days)', 'Waterskin'],
  "Entertainer's Pack": ['Backpack', 'Bedroll', '2 costumes', '5 candles', 'Rations (5 days)', 'Waterskin', 'Disguise kit'],
  "Explorer's Pack": ['Backpack', 'Bedroll', '2 flasks of oil', 'Rope (50 feet)', 'Tinderbox', 'Torch (10)', 'Rations (10 days)', 'Waterskin'],
  "Priest's Pack": ['Backpack', 'Blanket', 'Holy water (1 flask)', 'Rations (7 days)', 'Robe', 'Tinderbox', '2 candles', 'Waterskin'],
  "Scholar's Pack": ['Backpack', 'Book (lore)', 'Ink', 'Ink pen', 'Lamp', '10 sheets of parchment', 'Bag of sand', 'Small knife'],
}

/** Tool categories a background or class can make you choose from. */
export const MUSICAL_INSTRUMENTS = [
  'Bagpipes', 'Drum', 'Dulcimer', 'Flute', 'Horn', 'Lute', 'Lyre',
  'Pan flute', 'Shawm', 'Viol',
]

export const ARTISANS_TOOLS = [
  "Alchemist's supplies", "Brewer's supplies", "Calligrapher's supplies",
  "Carpenter's tools", "Cartographer's tools", "Cobbler's tools", "Cook's utensils",
  "Glassblower's tools", "Jeweler's tools", "Leatherworker's tools", "Mason's tools",
  "Painter's supplies", "Potter's tools", "Smith's tools", "Tinker's tools",
  "Weaver's tools", "Woodcarver's tools",
]

export const GAMING_SETS = ['Dice set', 'Dragonchess set', 'Playing card set', 'Three-Dragon Ante set']

/** Matches a "… of your choice" tool label to the list it draws from. */
export function toolOptionsFor(label: string): string[] {
  if (/musical instrument/i.test(label)) return MUSICAL_INSTRUMENTS
  if (/artisan/i.test(label)) return ARTISANS_TOOLS
  if (/gaming set/i.test(label)) return GAMING_SETS
  if (/artisan's tools or.*musical instrument/i.test(label)) {
    return [...ARTISANS_TOOLS, ...MUSICAL_INSTRUMENTS]
  }
  return []
}

/** SRD languages, with the Feywild-relevant ones first for this campaign. */
export const LANGUAGES = [
  'Sylvan', 'Elvish', 'Goblin', 'Gnomish', 'Halfling', 'Draconic', 'Dwarvish',
  'Giant', 'Orc', 'Abyssal', 'Celestial', 'Deep Speech', 'Infernal', 'Primordial',
  'Undercommon', 'Common Sign Language',
]
